import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import type { BusinessContext, DashboardData } from "../../shared/types";
import { apiRequest, loadDashboard } from "./api";
import { useAuth } from "./auth";
import { createPreviewDashboard } from "./demo-data";

type WorkspaceContextValue = {
  data: DashboardData | null;
  loading: boolean;
  error: string | null;
  notice: string | null;
  reload: () => Promise<void>;
  saveContext: (context: BusinessContext) => Promise<void>;
  setPaused: (paused: boolean) => Promise<void>;
  saveSettings: (timezone: string, paused: boolean) => Promise<void>;
  setChannelAutoReply: (channelId: string, enabled: boolean) => Promise<void>;
  dismissNotice: () => void;
};

const WorkspaceContext = createContext<WorkspaceContextValue | null>(null);

export function WorkspaceProvider({ children }: PropsWithChildren) {
  const { status, accessToken } = useAuth();
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const reload = useCallback(async () => {
    if (status === "preview") {
      setData(createPreviewDashboard());
      setError(null);
      return;
    }
    if (status !== "authenticated" || !accessToken) {
      setData(null);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      setData(await loadDashboard(accessToken));
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Could not load this workspace.");
    } finally {
      setLoading(false);
    }
  }, [accessToken, status]);

  useEffect(() => {
    // Fetching auth-dependent external state; the loading transition is intentional.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void reload();
  }, [reload]);

  const saveContext = useCallback(
    async (context: BusinessContext) => {
      if (status === "preview") {
        setData((current) =>
          current ? { ...current, context: { ...context, version: context.version + 1 } } : current,
        );
        setNotice("Preview changes saved in this browser session only.");
        return;
      }
      if (!accessToken) throw new Error("Your session has expired.");
      const saved = await apiRequest<BusinessContext>("/api/context", accessToken, {
        method: "PUT",
        body: JSON.stringify(context),
      });
      setData((current) => (current ? { ...current, context: saved } : current));
      setNotice("Business Context saved. Existing approvals now require review.");
    },
    [accessToken, status],
  );

  const saveSettings = useCallback(
    async (timezone: string, paused: boolean) => {
      if (status === "preview") {
        setData((current) =>
          current
            ? { ...current, settings: { ...current.settings, timezone, sendsPaused: paused } }
            : current,
        );
        setNotice(paused ? "All preview sends are paused." : "Preview pause lifted; no live sends are enabled.");
        return;
      }
      if (!accessToken) throw new Error("Your session has expired.");
      await apiRequest<{ sendsPaused: boolean }>("/api/settings", accessToken, {
        method: "PUT",
        body: JSON.stringify({ timezone, paused }),
      });
      setData((current) =>
        current
          ? { ...current, settings: { ...current.settings, timezone, sendsPaused: paused } }
          : current,
      );
      setNotice(paused ? "All outgoing sends are paused." : "Pause lifted. Overdue jobs still require review.");
    },
    [accessToken, status],
  );

  const setPaused = useCallback((paused: boolean) => saveSettings(data?.settings.timezone ?? 'Asia/Bangkok', paused), [data?.settings.timezone, saveSettings]);

  const setChannelAutoReply = useCallback(
    async (channelId: string, enabled: boolean) => {
      if (status !== "preview") {
        if (!accessToken) throw new Error("Your session has expired.");
        await apiRequest(`/api/channels/${channelId}/automation`, accessToken, {
          method: "PUT",
          body: JSON.stringify({ enabled }),
        });
      }
      setData((current) =>
        current
          ? {
              ...current,
              channels: current.channels.map((channel) =>
                channel.id === channelId
                  ? { ...channel, autoReplyEnabled: enabled }
                  : channel,
              ),
            }
          : current,
      );
      setNotice(
        enabled
          ? "Approved-FAQ auto-reply enabled for this channel. Sensitive and unknown questions still need review."
          : "Auto-reply disabled for this channel.",
      );
    },
    [accessToken, status],
  );

  const value = useMemo<WorkspaceContextValue>(
    () => ({
      data,
      loading,
      error,
      notice,
      reload,
      saveContext,
      saveSettings,
      setPaused,
      setChannelAutoReply,
      dismissNotice: () => setNotice(null),
    }),
    [data, error, loading, notice, reload, saveContext, saveSettings, setChannelAutoReply, setPaused],
  );

  return <WorkspaceContext.Provider value={value}>{children}</WorkspaceContext.Provider>;
}

export function useWorkspace(): WorkspaceContextValue {
  const value = useContext(WorkspaceContext);
  if (!value) throw new Error("useWorkspace must be used inside WorkspaceProvider.");
  return value;
}
